import { useEffect, useState, useCallback, useMemo } from 'react';
import { getMyPermissions } from '../services/dataPermissionsService';

/**
 * Hook for the current user's data access denials.
 *
 * Usage:
 *   const { loading, isAdmin, isDenied } = useDataPermissions();
 *   if (isDenied('REFINED_FOO')) ...                  // whole-schema denial
 *   if (isDenied('REFINED_FOO', 'V_BAR')) ...         // specific view denial
 *
 * Admins always get ``isDenied() === false``. On error we fail open
 * (every check returns false) so the UI keeps working even if the
 * manager-service is unreachable; the SQL preflight is the real
 * backstop for the AI chat.
 */
export default function useDataPermissions() {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [denials, setDenials] = useState([]);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMyPermissions();
      setIsAdmin(!!data.is_admin);
      setDenials(Array.isArray(data.denials) ? data.denials : []);
      setError(null);
    } catch (e) {
      setError(e);
      setIsAdmin(false);
      setDenials([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Build O(1) lookup sets from the denial list.
  const { schemaSet, tableSet } = useMemo(() => {
    const ss = new Set();
    const ts = new Set();
    for (const d of denials) {
      const s = (d.schema_name || '').trim().toUpperCase();
      const t = (d.table_name || '').trim().toUpperCase();
      if (!s) continue;
      if (!t) ss.add(s);
      else ts.add(`${s}.${t}`);
    }
    return { schemaSet: ss, tableSet: ts };
  }, [denials]);

  const isDenied = useCallback((schema, table) => {
    if (isAdmin) return false;
    const s = (schema || '').trim().toUpperCase();
    if (!s) return false;
    if (schemaSet.has(s)) return true;
    if (table) {
      const t = (table || '').trim().toUpperCase();
      if (tableSet.has(`${s}.${t}`)) return true;
    }
    return false;
  }, [isAdmin, schemaSet, tableSet]);

  return { loading, isAdmin, denials, isDenied, error, refresh };
}
